// /op/docs (rule 37): the documents in force that apply to whoever is signed in, on the tablet, and which they have
// read. Open one, read it, say so: the acknowledgement names the person on the token, so a shared login is refused by
// the server and is told why. Needs the network: a document read here must be the one in force now.
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { serverMessage, useResource } from '../../shells/OfficeShell';
import { FileViewer } from '../office/CertificateFile';
import type { MinePayload, MyDocument } from '../office/documentsModel';

const target = 'min-h-[56px] rounded-(--radius) border px-(--s4) font-semibold';

export function OperatorDocuments() {
  const res = useResource<MinePayload>('/documents/mine');
  const [reading, setReading] = useState<MyDocument | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => setFailed(null), [reading]);

  async function acknowledge(d: MyDocument) {
    setBusy(true);
    setFailed(null);
    try {
      await api.post(`/documents/${d.id}/versions/${d.versionId}/acknowledge`, { via: 'OPERATOR' });
      setReading(null);
      res.reload();
    } catch (err) {
      setFailed(serverMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (reading) {
    return (
      <div className="flex h-full min-h-[70dvh] flex-col gap-(--s3) p-(--s3)">
        <h1 className="m-0 text-(length:--fs-xl) font-bold">
          <span className="font-mono">{reading.docId}</span> {reading.title}
        </h1>
        <FileViewer path={`/documents/${reading.id}/file?version=${reading.versionId}`} label={`${reading.docId}.pdf`} title={reading.docId} />
        {failed ? (
          <p role="alert" className="m-0 font-semibold text-(--bad)">
            {failed}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-(--s2)">
          {reading.acknowledgedAt ? null : (
            <button type="button" disabled={busy} onClick={() => void acknowledge(reading)} className={`${target} border-(--border-strong) bg-(--primary) text-(--primary-fg)`}>
              I have read this
            </button>
          )}
          <button type="button" onClick={() => setReading(null)} className={`${target} border-(--border-strong) bg-(--bg)`}>
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-(--s3) p-(--s3)">
      <h1 className="m-0 text-(length:--fs-xl) font-bold">Documents</h1>
      {res.error ? (
        <div role="alert" className="flex flex-col gap-(--s2)">
          <p className="m-0 font-semibold text-(--warn)">Could not read your documents: {res.error} This is not the same as "none".</p>
          <div>
            <button type="button" onClick={res.reload} className={`${target} border-(--border-strong) bg-(--bg)`}>
              Try again
            </button>
          </div>
        </div>
      ) : !res.data ? (
        <p role="status" className="m-0 text-(--muted)">
          Loading…
        </p>
      ) : res.data.documents.length === 0 ? (
        <p className="m-0 text-(--muted)">No document in force applies to you.</p>
      ) : (
        <>
          <p className="m-0 font-semibold">{res.data.toRead ? `${res.data.toRead} to read` : 'All read.'}</p>
          <ul className="m-0 flex list-none flex-col gap-(--s2) p-0">
            {res.data.documents.map((d) => (
              <li key={d.id}>
                <button type="button" onClick={() => setReading(d)} className={`${target} flex w-full flex-wrap items-center justify-between gap-(--s2) border-(--border) bg-(--card) py-(--s2) text-left`}>
                  <span className="min-w-0 flex-1">
                    <span className="font-mono">{d.docId}</span> {d.title}
                  </span>
                  <span className={d.acknowledgedAt ? 'font-normal text-(--muted)' : 'text-(--warn)'}>{d.acknowledgedAt ? 'Read' : 'To read'}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export default OperatorDocuments;
